<template>
    <div class="change-company">
        <div class="container section">
            <div class="row">
                <div class="col-lg-4">
                    <div class="title">
                        Skift virksomhed
                    </div>
                    <br>
                    <p>
                        Som super admin kan du her ændre hvilken virksomhed du ønsker at se data for. Når du vælger en virksomhed, vil applikationen opdatere og du ser nu de samme data, som virksomhedens egne brugere ser det.
                    </p>
                    <p style="margin-top:24px;">
                        Tallet ud for hver virksomhed viser hvor mange svende der har registreret tid inden for de seneste 90 dage.
                    </p>

                    <div v-if="invalidApiKeyCount > 0" class="api-warning-banner">
                        <i class="fas fa-exclamation-triangle" />
                        {{ invalidApiKeyCount }} virksomhed{{ invalidApiKeyCount === 1 ? '' : 'er' }} med ugyldig API-nøgle
                    </div>

                    <div class="filters">
                        <div class="filter-group">
                            <div class="filter-label">Sortering</div>
                            <button :class="{ active: sortBy === 'name' }" @click="sortBy = 'name'">Alfabetisk</button>
                            <button :class="{ active: sortBy === 'svende_desc' }" @click="sortBy = 'svende_desc'">Flest svende</button>
                            <button :class="{ active: sortBy === 'newest' }" @click="sortBy = 'newest'">Senest oprettet</button>
                        </div>
                        <div class="filter-group">
                            <div class="filter-label">System</div>
                            <button :class="{ active: providerFilter === 'all' }" @click="providerFilter = 'all'">Alle</button>
                            <button :class="{ active: providerFilter === 'Ordrestyring' }" @click="providerFilter = 'Ordrestyring'">Ordrestyring</button>
                            <button :class="{ active: providerFilter === 'Minuba' }" @click="providerFilter = 'Minuba'">Minuba</button>
                        </div>
                        <div class="filter-group">
                            <div class="filter-label">Svende</div>
                            <button :class="{ active: svendeFilter === 'all' }" @click="svendeFilter = 'all'">Alle</button>
                            <button :class="{ active: svendeFilter === 'with' }" @click="svendeFilter = 'with'">Med svende</button>
                            <button :class="{ active: svendeFilter === 'without' }" @click="svendeFilter = 'without'">Uden svende</button>
                        </div>
                    </div>
                </div>
                <div class="col-lg-8">

                    <box-loading height="350" v-if="loading" />

                    <template v-else>
                        <div v-if="filteredCompanies.length === 0" style="color:#666;padding:20px;">
                            Ingen virksomheder matcher filtrene.
                        </div>

                        <div class="company" v-for="company in filteredCompanies" :key="company.uuid">
                            <div class="name">
                                <span v-if="company.api_key_status === 'invalid'" class="api-warning" :title="'Ugyldig API-nøgle: ' + (company.api_key_check_error || '?')">
                                    <i class="fas fa-exclamation-triangle" />
                                </span>
                                {{ company.name }}
                                <br>
                                <span v-if="company.api_provider.name === 'Ordrestyring'" style="background:#339861;color:#fff;font-size:12px;padding:2px 6px;margin-top:4px;display:inline-block;border-radius:3px;">
                                    {{ company.api_provider.name }}
                                </span>
                                <span v-else style="background:#852240;color:#fff;font-size:12px;padding:2px 6px;margin-top:4px;display:inline-block;border-radius:3px;">
                                    {{ company.api_provider.name }}
                                </span>
                                <span class="svende-badge" v-if="company.active_field_employees_90d > 0" :title="'Aktive svende (working_field=1) der har registreret tid de seneste 90 dage'">
                                    <i class="fas fa-user-hard-hat" /> {{ company.active_field_employees_90d }}
                                </span>
                            </div>
                            <div class="action" @click="changeCompany(company)">
                                Skift til denne virksomhed
                                <i class="fas fa-arrow-right" />
                            </div>
                        </div>
                    </template>

                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "../../../styles/vars.scss";

    .api-warning-banner {
        background: #fff3cd;
        color: #8a6d10;
        padding: 8px 12px;
        border-radius: $borderradius--main;
        font-size: 13px;
        margin-top: 24px;

        i {
            margin-right: 6px;
        }
    }

    .api-warning {
        color: #e67700;
        margin-right: 4px;
    }

    .filters {
        margin-top: 24px;

        .filter-group {
            margin-bottom: 12px;
        }

        .filter-label {
            font-size: 12px;
            font-weight: 600;
            color: #666;
            margin-bottom: 4px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        button {
            background: $color--white;
            color: $color--dark;
            border: 1px solid rgba(0, 0, 0, 0.15);
            padding: 4px 10px;
            border-radius: $borderradius--main;
            font-size: 13px;
            cursor: pointer;
            margin-right: 4px;
            margin-bottom: 4px;
            transition: $transition--main;

            &:hover {
                background: #f0f0f0;
            }

            &.active {
                background: $color--dark;
                color: $color--white;
                border-color: $color--dark;
            }
        }
    }

    .company {
        background: $color--white;
        padding: $padding--main;
        box-shadow: $boxshadow--main;
        border-radius: $borderradius--main;
        margin-bottom: $margin--small;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .name {
            font-weight: 600;

            .svende-badge {
                background: #f0f0f0;
                color: $color--dark;
                font-size: 12px;
                font-weight: 500;
                padding: 2px 6px;
                margin-top: 4px;
                margin-left: 6px;
                display: inline-block;
                border-radius: 3px;
            }
        }

        .action {
            background: $color--dark;
            color: $color--white;
            padding: 4px 10px;
            border-radius: $borderradius--main;
            cursor: pointer;
            opacity: 0;
            transition: $transition--main;

            &:hover {
                opacity: 1;
            }
        }

        &:hover .action {
            opacity: 1;
        }
    }

</style>

<script>
import * as companyService from '@/services/companyService'

export default {
    data () {
        return {
            loading: false,
            companies: null,
            providerFilter: 'all',
            svendeFilter: 'all',
            sortBy: 'name'
        }
    },
    computed: {
        invalidApiKeyCount () {
            if (!this.companies) return 0
            return this.companies.filter(c => c.api_key_status === 'invalid').length
        },
        filteredCompanies () {
            if (!this.companies) return []
            let list = this.companies.filter(company => {
                if (this.providerFilter !== 'all' && company.api_provider.name !== this.providerFilter) {
                    return false
                }
                let count = company.active_field_employees_90d || 0
                if (this.svendeFilter === 'with' && count === 0) return false
                if (this.svendeFilter === 'without' && count > 0) return false
                return true
            })
            let sorted = list.slice()
            if (this.sortBy === 'svende_desc') {
                sorted.sort((a, b) => (b.active_field_employees_90d || 0) - (a.active_field_employees_90d || 0) || a.name.localeCompare(b.name, 'en'))
            } else if (this.sortBy === 'newest') {
                sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0) || a.name.localeCompare(b.name, 'en'))
            } else {
                sorted.sort((a, b) => a.name.localeCompare(b.name, 'en'))
            }
            return sorted
        }
    },
    created () {
        this.getCompanies()
    },
    methods: {
        getCompanies () {
            this.loading = true
            let params = {
                include: 'api_provider'
            }
            companyService.findAll(params, (response) => {
                this.companies = response.data
                this.loading = false
            }, (err) => {
                console.log(err)
                this.loading = false
            })
        },
        changeCompany (company) {
            let body = {
                company_uuid: company.uuid
            }
            companyService.change(body, (response) => {
                this.$toastr('success', 'Virksomhed opdateret. Opdaterer siden.', 'Sådan!')
                setTimeout(() => {
                    window.location.href = '/'
                }, 1000)
            }, (err) => {
                console.log(err)
            })
        }
    }
}
</script>



// WEBPACK FOOTER //
// changeCompany.vue?4a0cbcf4